import {Suspense, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApiNa} from '@di-shared/api/di';
import {ApiResponseDailyTrendResponse, type ApiResponseVehicleInfo} from '@di-shared/api/di/__generated_na';
import {ERROR} from '@di-shared/const';
import DailyLineGraphError from '@di-shared/features/common/DailyLineGraphError';
import DailyLineGraph from '@di-shared/features/common/DailyLineGrpah';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {dayOfInterval, endDateOfMonth, parseDateWithFormat, startDateOfMonth} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQueries} from '@tanstack/react-query';

export interface SvgGraphProps {
  vin: string;
  selectedMonth: string;
}

export default function ({vin, selectedMonth}: SvgGraphProps) {
  return (
    <ErrorBoundary fallback={<SafeDrivingDailyGraphNaError />}>
      <Suspense fallback={<SafeDrivingDailyGraphNaLoading />}>
        <Inner
          vin={vin}
          selectedMonth={selectedMonth}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedMonth}: SvgGraphProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const [{data: dailyTrendData}, {data: consentData}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApiNa.safeDriving.dailyTrend1), vin, selectedMonth],
        queryFn: () => diApiNa.safeDriving.dailyTrend1({vin, month: selectedMonth}),
        select: ({data}: ApiResponseDailyTrendResponse) => {
          return {
            ...data,
            dailyScoreList: [...data.dailyScoreList].reverse(),
          };
        },
      },
      {
        queryKey: [getQueryKey(diApiNa.vehicle.getVehicleInfo), vin],
        queryFn: () => diApiNa.vehicle.getVehicleInfo({vin}),
        select: ({data}: ApiResponseVehicleInfo) => {
          return {
            ...data,
            safeDrivingConsentData: parseDateWithFormat(
              data.consentedTerms.find((consentedTerm) => consentedTerm.termsCode === 'SAFETY_SCORE')?.consentDate
            ),
          };
        },
      },
    ],
  });

  const [selectedDataIndex, setSelectedDataIndex] = useState<number>(
    consentData.safeDrivingConsentData &&
      consentData.safeDrivingConsentData.getMonth() === new Date().getMonth() &&
      consentData.safeDrivingConsentData.getFullYear() === new Date().getFullYear()
      ? dailyTrendData.dailyScoreList.length - 1
      : 0
  );

  function getBarLineColor(selectedDataIndex: number) {
    const currentScore = dailyTrendData.dailyScoreList[selectedDataIndex].score;

    if (currentScore === null) {
      return 'var(--on-surface-container-mid)';
    }
    if (currentScore <= 74) {
      return 'var(--quaternary-low)';
    } else if (currentScore <= 88) {
      return 'var(--tertiary)';
    } else {
      return 'var(--graph-gauge-active)';
    }
  }

  function getTooltipText(selectedDataIndex: number) {
    const selectedScoreDate = parseDateWithFormat(dailyTrendData.dailyScoreList[selectedDataIndex].date);
    if (!selectedScoreDate) {
      throw Error('Invalid Date');
    }
    return `${CldrDatetimeFormatter.format(selectedScoreDate, 'MMMd')}┃${dailyTrendData.dailyScoreList[selectedDataIndex].score}p`;
  }

  function SafeDrivingMessage() {
    const currentScore = dailyTrendData.dailyScoreList[selectedDataIndex];
    if (currentScore === null) {
      return null;
    }
    return currentScore.score <= 74 ? (
      <div className="section_container_header">
        <h4
          className="body_xlarge_b"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_dss_LN_keep_higher_score')}
        </h4>
      </div>
    ) : currentScore.score <= 88 ? (
      <div className="section_container_header">
        <h4
          className="body_xlarge_b"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_dss_LN_nearly_at_the_top')}
        </h4>
      </div>
    ) : (
      <div className="section_container_header">
        <h4
          className="body_xlarge_b"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_dss_LN_you_have_outdone_yourself')}
        </h4>
      </div>
    );
  }

  return (
    <div className="section_container">
      <SafeDrivingMessage />

      <DailyLineGraph
        data={dailyTrendData.dailyScoreList.map((dailyTrendItem) => {
          return {
            score: dailyTrendItem.score,
            scoreDate: parseDateWithFormat(dailyTrendItem.date)!,
          };
        })}
        labels={getDaysInMonth(parseDateWithFormat(selectedMonth))}
        options={{
          max: 100,
          tickCount: 4,
          barLine: {
            color: getBarLineColor(selectedDataIndex),
            strokeWidth: '2',
            strokeDashArray: '3,3',
          },
          tooltip: {
            isShow: true,
            tooltipText: getTooltipText(selectedDataIndex),
          },
        }}
        interactions={{
          selectedDataIndex,
          onUpdateSelectedDataIndex: setSelectedDataIndex,
        }}
      />
      <ul className="statistics_info_list">
        <li className="statistics_info_item">
          <span className="statistics_info_key">{t('ctn_dss_LN_max')}</span>
          <span className="statistics_info_val">
            <span className="statistics_info_number">{dailyTrendData.scoreMax}</span>
            <span className="statistics_info_unit">p</span>
          </span>
        </li>
        <li className="statistics_info_item">
          <span className="statistics_info_key">{t('ctn_dss_LN_min')}</span>
          <span className="statistics_info_val">
            <span className="statistics_info_number">{dailyTrendData.scoreMin}</span>
            <span className="statistics_info_unit">p</span>
          </span>
        </li>
      </ul>
    </div>
  );
}

function getDaysInMonth(yyyyMMDate: Date | null): Array<Date> {
  if (!yyyyMMDate) {
    return [];
  }
  const start = startDateOfMonth(yyyyMMDate);
  const end = endDateOfMonth(yyyyMMDate);
  return dayOfInterval(start, end);
}

export function SafeDrivingDailyGraphNaLoading() {
  const {t} = useInferableTranslation();

  return (
    <div className="section_container skeleton">
      <div className="section_container_header">
        <h4
          className="body_xlarge_b"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_dss_LN_generating_daily_score')}
        </h4>
      </div>
      <DailyLineGraphError />
    </div>
  );
}

export function SafeDrivingDailyGraphNaError() {
  const {t} = useInferableTranslation();
  return (
    <div className="section_container">
      <div className="section_container_header">
        <h4
          className="body_xlarge_b"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('msg_dss_LN_generating_daily_score')}
        </h4>
      </div>
      <DailyLineGraphError />
      <ul className="statistics_info_list">
        <li className="statistics_info_item">
          <span className="statistics_info_key">{t('ctn_dss_LN_max')}</span>
          <span className="statistics_info_val">
            <span className="statistics_info_number">{ERROR.DASH}</span>
          </span>
        </li>
        <li className="statistics_info_item">
          <span className="statistics_info_key">{t('ctn_dss_LN_min')}</span>
          <span className="statistics_info_val">
            <span className="statistics_info_number">{ERROR.DASH}</span>
          </span>
        </li>
      </ul>
    </div>
  );
}
